import { Injectable, NgZone, inject, signal } from '@angular/core';
import { ISocketMessage } from 'modelos/src';
import { BehaviorSubject, Observable, Subject, Subscription } from 'rxjs';
import { WebSocketSubject, webSocket } from 'rxjs/webSocket';
import { Socket, io } from 'socket.io-client';

import { LoginService } from '../../modulos/login/login.service';
import { USE_WS_IO, WS, WS_IO } from '../../../environments/environment';

/**
 * Modo de entrega de una suscripción (server: rooms.ts).
 * - `completo` (default): un update por mutación con el body entero.
 * - `trigger`: el server batchea y recorta el body a lo mínimo para decidir
 *   si refrescar. Solo para consumidores que usan el mensaje como aviso y
 *   después releen por HTTP.
 */
export type ModoEntidad = 'completo' | 'trigger';

/** Filtro server-side: campo → valores aceptados (allowlist por entidad). */
export type FiltroEntidad = Record<string, string[]>;

export interface IOpcionesEntidad {
  modo?: ModoEntidad;
  filtro?: FiltroEntidad;
}

interface ISuscripcionEntidad {
  /** _id del permiso activo con el que el server filtra los updates. */
  idPermiso?: string;
  /**
   * Opciones de CADA consumidor, una entrada por ref. El server guarda UNA
   * suscripción por socket y entidad, así que lo que se emite es la unión
   * (ver combinarOpciones): con varios consumidores gana siempre el más
   * amplio, para que nadie pierda mensajes en silencio.
   */
  consumidores: IOpcionesEntidad[];
}

/**
 * Combina las opciones de todos los consumidores de una entidad.
 *
 * - `modo`: 'trigger' solo si TODOS lo piden. Uno solo que necesite el body
 *   completo obliga a 'completo' — el recorte del trigger le rompería el
 *   delta.
 * - `filtro`: la unión de los valores por campo, y un consumidor SIN filtro
 *   (o que no filtra ese campo) anula el filtro entero: quiere todo.
 */
function combinarOpciones(consumidores: IOpcionesEntidad[]): IOpcionesEntidad {
  const modo: ModoEntidad = consumidores.every((c) => c.modo === 'trigger')
    ? 'trigger'
    : 'completo';

  if (!consumidores.length || consumidores.some((c) => !c.filtro)) {
    return { modo, filtro: undefined };
  }

  // Un campo solo sobrevive si TODOS los consumidores lo filtran; si alguno
  // no lo menciona, ese alguien acepta cualquier valor de ese campo.
  const campos = Object.keys(consumidores[0].filtro ?? {}).filter((campo) =>
    consumidores.every((c) => c.filtro?.[campo]?.length),
  );
  if (!campos.length) {
    return { modo, filtro: undefined };
  }

  const filtro: FiltroEntidad = {};
  for (const campo of campos) {
    filtro[campo] = [
      ...new Set(consumidores.flatMap((c) => c.filtro?.[campo] ?? [])),
    ];
  }
  return { modo, filtro };
}

@Injectable({
  providedIn: 'root',
})
export class WebSocketService {
  // El socket se crea y procesa FUERA de Angular zone: llegan muchísimos mensajes
  // por segundo (mapa con subscribe-all) y, dentro de zone, cada mensaje dispararía
  // un ciclo de change detection de toda la app (~56% de CPU en el profiling).
  // Los consumidores que necesitan refrescar UI usan detectChanges() explícito.
  private ngZone = inject(NgZone);

  private isConnected = false;
  private socketMsg$ = new Subject<ISocketMessage>();

  /**
   * Estado de conexión observable por los servicios "vivos" para decidir la
   * cadencia de reconciliación (WS conectado → polling lento).
   */
  public connected = signal(false);
  private connectedSubject = new BehaviorSubject<boolean>(false);
  public connected$ = this.connectedSubject.asObservable();

  private setConnected(value: boolean) {
    this.isConnected = value;
    this.connected.set(value);
    this.connectedSubject.next(value);
  }

  // ── Estado de suscripciones (se reenvían automáticamente al reconectar) ──
  private subscribedVehicles = new Set<string>();
  // Suscripción única a TODOS los vehículos del scope (mapa de vehículos).
  // Guarda el _id del permiso activo; null = no suscrito.
  private allVehiclesPermiso: string | null = null;
  // Entidades whitelisted suscritas (subscribe-entity) → permiso usado y
  // cantidad de consumidores. El refcount es obligatorio: varios servicios
  // piden la MISMA entidad (ej: 'eventogenerico' lo usan el listado vivo y el
  // contador de pendientes del navbar, más el listado de servicio técnico) y
  // el server guarda una sola suscripción por socket. Sin contar refs, el
  // primer unsubscribe dejaba mudos a todos los demás.
  private subscribedEntities = new Map<string, ISuscripcionEntidad>();
  // Entidades cuyo subscribe-entity fue ACEPTADO por el server (ack success).
  // Los vivos solo relajan su polling si la suscripción está confirmada
  // (ej: 'alertas' es rechazada hasta que el server la soporte → siguen a 30s).
  private entidadesConfirmadas = new Set<string>();
  private entidadConfirmadaSubject = new Subject<string>();
  public entidadConfirmada$ = this.entidadConfirmadaSubject.asObservable();

  public isEntitySubscribed(entidad: string): boolean {
    return this.entidadesConfirmadas.has(entidad);
  }

  // ── WS v2 (socket.io) ──
  private ioSocket?: Socket;

  // ── WS legacy (ws crudo + rxjs/webSocket) ──
  private socket$?: WebSocketSubject<any>;
  private timer?: NodeJS.Timeout;
  private connecting = false;
  private socketSub$?: Subscription;

  constructor() {
    this.initWs();
    if (!USE_WS_IO) {
      this.initPing();
    }
  }

  public async initWs() {
    if (USE_WS_IO) {
      this.initIo();
    } else {
      await this.initWsLegacy();
    }
  }

  public getMessage(): Observable<ISocketMessage> {
    return this.socketMsg$.asObservable();
  }

  // ******************************************** //
  // WS v2 — socket.io (gestion-api-websocket-io)
  // ******************************************** //

  private initIo() {
    if (this.ioSocket) {
      return;
    }
    if (!WS_IO?.url) {
      console.warn('[WebSocket] WS_IO no configurado');
      return;
    }

    // Crear el socket FUERA de Angular zone (ver comentario de la clase).
    this.ngZone.runOutsideAngular(() => {
      this.ioSocket = io(WS_IO.url, {
        path: WS_IO.path,
        transports: ['websocket', 'polling'],
        // auth como callback: cada intento de (re)conexión lee el token FRESCO
        // de sessionStorage. Si el server rechaza (connect_error 'No
        // autorizado'), el refresh del HttpClientService (por el polling de
        // reconciliación) renueva el token y el próximo retry ya lo usa.
        auth: (cb) => {
          cb({ token: `Bearer ${LoginService.getAccessToken()}` });
        },
      });

      this.ioSocket.on('connect', () => {
        this.setConnected(true);
        // Una reconexión crea un socket server-side NUEVO: re-emitir TODAS
        // las suscripciones o el canal queda mudo (y el polling de
        // reconciliación lo enmascararía).
        this.resubscribeIo();
      });

      this.ioSocket.on('disconnect', () => {
        this.setConnected(false);
        // Las confirmaciones mueren con el socket server-side.
        this.entidadesConfirmadas.clear();
      });

      this.ioSocket.on('connect_error', (error) => {
        console.warn(`[WebSocket] connect_error: ${error?.message}`);
      });

      this.ioSocket.on('update', (message: ISocketMessage) => {
        this.socketMsg$.next(message);
      });
    });
  }

  private resubscribeIo() {
    if (this.allVehiclesPermiso !== null) {
      this.ioSocket?.emit('subscribe-all-vehicles', {
        idPermiso: this.allVehiclesPermiso,
      });
    }
    this.subscribedVehicles.forEach((vehicleId) => {
      this.ioSocket?.emit('subscribe-vehicle', { vehicleId });
    });
    this.subscribedEntities.forEach((info, entidad) => {
      this.emitSubscribeEntity(entidad, info.idPermiso);
    });
  }

  private emitSubscribeEntity(entidad: string, idPermiso?: string) {
    const info = this.subscribedEntities.get(entidad);
    const { modo, filtro } = combinarOpciones(info?.consumidores ?? []);
    this.ioSocket?.emit(
      'subscribe-entity',
      { entidad, idPermiso, modo, filtro },
      (ack: { success: boolean; message?: string }) => {
        if (ack?.success) {
          this.entidadesConfirmadas.add(entidad);
          this.entidadConfirmadaSubject.next(entidad);
        } else {
          // El server rechaza (no degrada en silencio) un modo o un filtro que
          // la entidad no soporta. Sin confirmación, los vivos se quedan con su
          // cadencia de polling rápida, que es el comportamiento seguro.
          this.entidadesConfirmadas.delete(entidad);
          console.warn(
            `[WebSocket] subscribe-entity ${entidad} rechazado: ${ack?.message}`,
          );
        }
      },
    );
  }

  // ******************************************** //
  // Suscripciones (API pública común a ambos modos)
  // ******************************************** //

  /**
   * Suscribe a un vehículo para recibir actualizaciones en tiempo real
   * Si el WebSocket no está conectado, guarda la suscripción para reenviarla al conectar
   */
  public subscribeVehicle(vehicleId: string): void {
    this.subscribedVehicles.add(vehicleId);

    if (!this.isConnected) {
      console.warn(
        `[WebSocket] ⏳ Guardando suscripción pendiente para vehículo ${vehicleId} (se enviará al conectar)`,
      );
      return;
    }

    if (USE_WS_IO) {
      this.ioSocket?.emit('subscribe-vehicle', { vehicleId });
    } else {
      this.socket$?.next({ event: 'subscribe-vehicle', data: { vehicleId } });
    }
  }

  /**
   * Desuscribe de un vehículo
   */
  public unsubscribeVehicle(vehicleId: string): void {
    this.subscribedVehicles.delete(vehicleId);

    if (!this.isConnected) {
      return;
    }

    if (USE_WS_IO) {
      this.ioSocket?.emit('unsubscribe-vehicle', { vehicleId });
    } else {
      this.socket$?.next({
        event: 'unsubscribe-vehicle',
        data: { vehicleId },
      });
    }
  }

  /**
   * Suscribe a TODOS los vehículos del scope del usuario con un solo mensaje.
   * Reemplaza el loop de subscribe-vehicle en el mapa de vehículos.
   * El backend filtra cada reporte por el permiso (idPermiso = _id, header x-permiso).
   */
  public subscribeAllVehicles(idPermiso: string): void {
    this.allVehiclesPermiso = idPermiso;

    if (!this.isConnected) {
      console.warn(
        '[WebSocket] ⏳ subscribe-all-vehicles pendiente (se enviará al conectar)',
      );
      return;
    }

    if (USE_WS_IO) {
      this.ioSocket?.emit('subscribe-all-vehicles', { idPermiso });
    } else {
      this.socket$?.next({
        event: 'subscribe-all-vehicles',
        data: { idPermiso },
      });
    }
  }

  /**
   * Desuscribe de la suscripción única a todos los vehículos.
   */
  public unsubscribeAllVehicles(): void {
    this.allVehiclesPermiso = null;

    if (!this.isConnected) {
      return;
    }

    if (USE_WS_IO) {
      this.ioSocket?.emit('unsubscribe-all-vehicles', {});
    } else {
      this.socket$?.next({ event: 'unsubscribe-all-vehicles', data: {} });
    }
  }

  /**
   * Suscribe a los cambios de una entidad whitelisted en el server
   * (eventogenerico, dispositivoalarmas, materialesserviciotecnico, alertas).
   * Solo disponible en WS v2 (socket.io); en legacy es un no-op silencioso
   * (los listados siguen funcionando por polling).
   *
   * Los mensajes llegan por getMessage() con motivo 'entidad-suscrita' y
   * paths [entidad]. El server filtra por permiso (idPermiso = _id del
   * permiso activo, header x-permiso).
   */
  public subscribeEntity(
    entidad: string,
    idPermiso?: string,
    opciones: IOpcionesEntidad = {},
  ): void {
    const actual = this.subscribedEntities.get(entidad);

    if (!actual) {
      this.subscribedEntities.set(entidad, {
        idPermiso,
        consumidores: [opciones],
      });
      if (USE_WS_IO && this.isConnected) {
        this.emitSubscribeEntity(entidad, idPermiso);
      }
      return;
    }

    // Ya hay otro consumidor: suma la referencia. Si sus opciones ensanchan
    // lo suscrito (pide el body completo, o no filtra un campo que el resto
    // sí filtra), hay que re-emitir: el server guarda UNA suscripción por
    // socket y con la anterior este consumidor perdería mensajes.
    const antes = JSON.stringify(combinarOpciones(actual.consumidores));
    actual.consumidores.push(opciones);
    const despues = JSON.stringify(combinarOpciones(actual.consumidores));
    if (antes !== despues && USE_WS_IO && this.isConnected) {
      // subscribe-entity es idempotente en el server (pisa la entrada del socket).
      this.emitSubscribeEntity(entidad, actual.idPermiso);
    }
  }

  /**
   * Actualiza el permiso con el que el server filtra una entidad ya suscrita,
   * SIN tocar el refcount. Lo usa el cambio de permiso activo: la suscripción
   * sigue viva pero el server la seguiría filtrando con el permiso viejo
   * (mostrando el scope anterior y ocultando el nuevo) hasta reconectar.
   */
  public setEntityPermiso(entidad: string, idPermiso?: string): void {
    const actual = this.subscribedEntities.get(entidad);
    if (!actual || actual.idPermiso === idPermiso) {
      return;
    }
    actual.idPermiso = idPermiso;

    if (!USE_WS_IO || !this.isConnected) {
      return;
    }

    // subscribe-entity es idempotente en el server (pisa la entrada del socket).
    this.emitSubscribeEntity(entidad, idPermiso);
  }

  public unsubscribeEntity(
    entidad: string,
    opciones: IOpcionesEntidad = {},
  ): void {
    const actual = this.subscribedEntities.get(entidad);
    if (!actual) {
      return;
    }

    // Saca la entrada de ESTE consumidor (la primera que coincida); si no
    // matchea ninguna, saca la última para no dejar refs colgadas.
    const buscado = JSON.stringify(opciones);
    const i = actual.consumidores.findIndex(
      (c) => JSON.stringify(c) === buscado,
    );
    actual.consumidores.splice(
      i >= 0 ? i : actual.consumidores.length - 1,
      1,
    );

    if (actual.consumidores.length > 0) {
      // Queda al menos un consumidor: la entidad sigue suscrita, pero sin el
      // que se fue la unión puede angostarse (menos campos, o volver a
      // trigger) y conviene re-emitir para dejar de recibir de más.
      if (USE_WS_IO && this.isConnected) {
        this.emitSubscribeEntity(entidad, actual.idPermiso);
      }
      return;
    }

    this.subscribedEntities.delete(entidad);
    this.entidadesConfirmadas.delete(entidad);

    if (!USE_WS_IO || !this.isConnected) {
      return;
    }

    this.ioSocket?.emit('unsubscribe-entity', { entidad });
  }

  public closeWS() {
    if (USE_WS_IO) {
      this.ioSocket?.disconnect();
      this.ioSocket = undefined;
      this.setConnected(false);
      return;
    }
    this.socket$?.complete();
    this.socketSub$?.unsubscribe();
  }

  // ******************************************** //
  // WS legacy — ws crudo (gestion-websocket)
  // ******************************************** //

  private initPing() {
    // Ping de prueba (fuera de zone para no disparar CD cada 30s)
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = undefined;
    }
    this.ngZone.runOutsideAngular(() => {
      this.timer = setInterval(() => {
        if (this.isConnected && this.socket$) {
          this.socket$.next({
            event: 'ping',
            data: 'ping',
          });
        }
      }, 30000);
    });
  }

  private async initWsLegacy() {
    if (this.isConnected && this.socket$) {
      return;
    }

    if (this.connecting) {
      return;
    }

    this.connecting = true;
    const token = await LoginService.getAccessToken();
    if (token && WS) {
      try {
        // Crear y suscribir el socket FUERA de Angular zone: así los eventos del
        // WebSocket (y todo el procesamiento downstream) NO disparan change detection
        // por cada mensaje. La UI que dependa de esto refresca con detectChanges().
        this.ngZone.runOutsideAngular(() => {
          this.socket$ = webSocket({
            url: WS,
            openObserver: {
              next: () => {
                this.setConnected(true);
                this.sendToken(token);
                // Reenviar suscripciones pendientes
                this.resubscribeVehicles();
              },
            },
            closeObserver: {
              next: () => {
                this.setConnected(false);
              },
            },
          });

          this.socketSub$ = this.socket$.subscribe(
            (message: ISocketMessage) => this.handleMessage(message),
            () => this.handleError(),
            () => this.closed(),
          );
        });
      } catch (error) {
        console.error(`[WebSocket] Error al iniciar WebSocket ${error}`);
      }
    } else {
      console.warn('[WebSocket] No hay token o websocketServer');
    }

    this.connecting = false;
  }

  private sendToken(token: string) {
    // Envio de mensaje con el token
    const identity = {
      event: 'identity',
      data: `Bearer ${token}`,
    };
    this.socket$?.next(identity);
  }

  /**
   * Reenvía todas las suscripciones de vehículos al reconectar
   */
  private resubscribeVehicles(): void {
    // Reenviar suscripción única a todos (si está activa)
    if (this.allVehiclesPermiso !== null) {
      this.socket$?.next({
        event: 'subscribe-all-vehicles',
        data: { idPermiso: this.allVehiclesPermiso },
      });
    }

    if (this.subscribedVehicles.size === 0) {
      return;
    }

    this.subscribedVehicles.forEach((vehicleId) => {
      this.socket$?.next({
        event: 'subscribe-vehicle',
        data: { vehicleId },
      });
    });
  }

  // WS Handlers

  private async handleMessage(message: ISocketMessage) {
    this.socketMsg$.next(message);
  }

  private handleError() {
    console.warn('[WebSocket] Error - reconectando...');
    this.socketSub$?.unsubscribe();
    setTimeout(async () => {
      await this.initWs();
    }, 2000);
  }

  private closed() {
    this.socketSub$?.unsubscribe();
    this.setConnected(false);
  }
}
